import React from "react";
import { IShardRepository } from "../../../../api/entities/shard-repository";
import { CONFIG } from "../../../../config/config";
import { FormMode, IFormField } from "../../../../utils/form-utils";
import { storageTypeToNameMap, storageUtils } from "../../../../utils/storage-utils";
import Button from "../../../shared-ui/button";
import { createRemoveColumn } from "../../../shared-ui/compact-table/columns/remove-column";
import { CompactTable } from "../../../shared-ui/compact-table/compact-table";
import FormSection from "../../../shared-ui/form/form-section/form-section";
import Checkbox from "../../../shared-ui/input/checkbox";
import { IColumnDefinition } from "../../../shared-ui/table/table";
import tableStyles from "../../../shared-ui/table/table.module.scss";
import { IShardRepositoryTypes } from "../../../shard-repositories/sections/shard-repository-section/shard-repository-section";
import { MoreOption } from "./moreoption";
import styles from "./shard-repositories-section.module.scss";

export interface IShardClusterRepository {
  shardRepository: IShardRepository;
  types: IShardRepositoryTypes;
}

export interface IShardRepositoriesSectionProps {
  mode: FormMode;
  shardClusterRepositories: IFormField<IShardClusterRepository[]>;

  onEditStorageNode?: (node: IShardClusterRepository) => void;
  onOpenSelectStorageNodesPanel?: () => void;
}

export function validateShardClusterRepositories(shardRepositories: IShardClusterRepository[]) {
  if ( 
    shardRepositories.filter(n => n.types.storeContent).length < CONFIG.minStoragesCount ||
    shardRepositories.filter(n => n.types.storeKey).length < CONFIG.minStoragesCount ||
    shardRepositories.filter(n => n.types.storeMetadata).length < CONFIG.minStoragesCount
  ) {
    return `Myota requires at least ${CONFIG.minStoragesCount} shard repositories configured for content, key, and metadata.`;
  }

  if ( 
    shardRepositories.filter(n => n.types.storeContent).length > CONFIG.maxStoragesCount ||
    shardRepositories.filter(n => n.types.storeKey).length > CONFIG.maxStoragesCount ||
    shardRepositories.filter(n => n.types.storeMetadata).length > CONFIG.maxStoragesCount
  ) {
    return `Maximum allowed shard repositories for content, key, and metadata is ${CONFIG.maxStoragesCount}.`;
  }

  const nodeWithoutType = shardRepositories.find(n => !n.types.storeContent && !n.types.storeKey && !n.types.storeMetadata)
  if (nodeWithoutType) {
    return `Please select Storage Type for shard repository '${nodeWithoutType.shardRepository.bucket}'`;
  }
  return null;
}

export const ShardRepositoriesSection = (props: IShardRepositoriesSectionProps) => {

  function deleteStorageNode(poolNode: IShardClusterRepository) {
    props.shardClusterRepositories.onChange(props.shardClusterRepositories.value.filter(n => n !== poolNode));
  }

  const columns: IColumnDefinition<IShardClusterRepository, null>[] = [
    {
      testId: "col-bucket",
      className: styles["col-bucket"],
      label: "Bucket",
      formatter: (poolNode) => (
        <>
            <Button testId="name-button" className={styles["node-link"]} colors="link" onClick={() => !!props?.onEditStorageNode && props?.onEditStorageNode(poolNode)}>
              {poolNode.shardRepository.bucket}
            </Button>
        </>
      )
    },
    {
      testId: "col-type",
      className: styles["col-type"],
      label: "Type",
      formatter: (poolNode) => (
        <>
          {storageTypeToNameMap[storageUtils.getStorageProviderTypeGeneralizedType(poolNode.shardRepository.storageType)]}
        </>
      )
    },
    {
      testId: "col-region",
      className: styles["col-region"],
      label: "Region",
      formatter: (poolNode) => (
          <>
              {poolNode.shardRepository.region}
          </>
      )
    }
  ];


  let isCheckboxDisabled = props.mode !== FormMode.Create;

  columns.push(
        {
            testId: "col-content",
            className: styles["col-content"],
            label: "Content",
            formatter: poolNode =>
                <Checkbox
                    testId="content-checkbox"
                    checked={poolNode.types.storeContent}
                    disabled={isCheckboxDisabled}
                    onChange={(value) => {
                        poolNode.types.storeContent = value;
                        props.shardClusterRepositories.onChange([...props.shardClusterRepositories.value]);
                    }}
                />
        },
        {
            testId: "col-key",
            className: styles["col-key"],
            label: "Key",
            formatter: poolNode =>
                <Checkbox
                    testId="key-checkbox"
                    checked={poolNode.types.storeKey}
                    disabled={isCheckboxDisabled}
                    onChange={(value) => {
                        poolNode.types.storeKey = value;
                        props.shardClusterRepositories.onChange([...props.shardClusterRepositories.value]);
                    }}
                />
        },
        {
            testId: "col-metadata",
            className: styles["col-metadata"],
            label: "Metadata",
            formatter: poolNode =>
              <Checkbox
                  testId="metadata-checkbox"
                  checked={poolNode.types.storeMetadata}
                  disabled={isCheckboxDisabled}
                  onChange={(value) => {
                    poolNode.types.storeMetadata = value;
                    props.shardClusterRepositories.onChange([...props.shardClusterRepositories.value]);
                  }}
              />
        });
  if (props.mode === FormMode.Create) {
     columns.push(createRemoveColumn<IShardClusterRepository>(deleteStorageNode));
  }


  if (props.mode === FormMode.Edit) {
    columns.push({
      testId: "col-manage",
      className: tableStyles["col-manage"],
      label: "Manage",
      formatter: (poolNode) => (
        <>
            <MoreOption
              storagePoolNode={poolNode}
              onEditStorageNode={props.onEditStorageNode}
            />
        </>
      )
    });
  }

  function getTable() {
    return <CompactTable
        testId="storage-pool-nodes-table"
        className={styles.table}
        loading={false}
        columns={columns}
        rows={props.shardClusterRepositories.value}
        rowId={(shardClusterRepository) => shardClusterRepository.shardRepository.id}
        onAddClick={props.mode === FormMode.Create ? props.onOpenSelectStorageNodesPanel : null}
        addButtonCaption={'Select Shard Repository'}
    />
  }

  function getRootElement(children: React.JSX.Element) {
    if (props.mode === FormMode.ReadOnly) {
      return children;
    }

    let sectionTitle = props.mode === FormMode.Create ? 'Add Shard Repositories' : 'Shard Repositories';
    let sectionDescription = props.mode === FormMode.Create ? `Myota requires at least ${CONFIG.minStoragesCount} shard repositories.` : '';

    return (
        <FormSection
            testId="add-shard-repositories-section"
            title={sectionTitle}
            description={sectionDescription}
        >
          {children}
        </FormSection>
    );
  }

  return getRootElement(getTable());
};
